import React, { useState, useEffect } from 'react';
import { FaEye, FaEdit, FaTrash, FaSearch, FaTools, FaMapMarkerAlt, FaStar, FaSpinner, FaUser, FaEnvelope, FaPhone, FaLock, FaIdCard, FaStore, FaClock, FaCog, FaCopy, FaChevronDown, FaChevronUp, FaTimesCircle, FaFileAlt } from 'react-icons/fa';
import { mechanicGarageService, getDocumentUrl } from '../../../../services/apiService';
import AdminLocationDropdown from '../components/AdminLocationDropdown';
import { getDisplayRole, getProfileDisplayEmail, getProfileDisplayName } from '../../../../utils/displayName';
import { toast } from 'react-toastify';
import GarageClusterVerificationPanel from '../components/GarageClusterVerificationPanel';
import { CLUSTERS } from '../../../../constants/garageServiceClusters';

const isDocumentUrl = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') return /^\/uploads\//.test(v) || /^uploads\//.test(v) || /^https?:\/\//.test(v);
  if (typeof v === 'object' && v !== null && typeof v.path === 'string') return true;
  return false;
};
const getDocumentValue = (v) => (v == null ? '' : typeof v === 'object' && v !== null && v.path != null ? v.path : String(v));

const DataRow = ({ label, value, icon: Icon, copyable, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (value == null || value === '') return;
    const str = Array.isArray(value) ? value.join(', ') : String(value);
    if (str === '••••••••') return;
    navigator.clipboard?.writeText(str).then(() => {
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const documentUrl = getDocumentValue(value);
  const isDoc = isDocumentUrl(value);
  const display = value == null ? '—' : value === true ? 'Yes' : value === false ? 'No' : Array.isArray(value) ? (value.length ? value.join(', ') : '—') : isDoc ? 'View document' : String(value);
  const showCopy = copyable && display && display !== '—' && display !== '••••••••' && !isDoc;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 group">
      {Icon && <div className="mt-0.5 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0 group-hover:bg-gray-200 transition-colors"><Icon className="text-sm" /></div>}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        {isDoc && documentUrl ? (
          <a href={getDocumentUrl(documentUrl)} target="_blank" rel="noopener noreferrer" className="mt-0.5 text-sm font-medium text-sky-600 hover:underline break-words inline-flex items-center gap-1">
            <FaFileAlt className="text-xs shrink-0" /> View document
          </a>
        ) : (
          <p className="mt-0.5 text-sm font-medium text-gray-900 break-words">{display}</p>
        )}
      </div>
      {showCopy && (
        <button
          type="button"
          onClick={handleCopy}
          className="mt-0.5 p-2 rounded-lg text-gray-400 hover:text-slate-600 hover:bg-slate-100 transition-all shrink-0"
          title="Copy"
        >
          <FaCopy className={`text-xs ${copied ? 'text-emerald-500' : ''}`} />
        </button>
      )}
    </div>
  );
};

const Garages = () => {
  const [view, setView] = useState('list'); // 'list', 'edit', 'view'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(true);
  const [expandedGarage, setExpandedGarage] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState(() => {
    try {
      const raw = localStorage.getItem('adminLocation') || 'all';
      if (raw === 'all' || !raw) return 'all';
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : raw;
      } catch (_) {
        return raw;
      }
    } catch (e) { return 'all'; }
  });

  const [formData, setFormData] = useState({
    name: '', email: '', location: '', phone: '', password: '', status: 'Active'
  });

  // Fetch garages from API
  useEffect(() => {
    fetchGarages();
  }, []);

  const fetchGarages = async () => {
    try {
      setLoading(true);
      const response = await mechanicGarageService.getAllGarages();
      // Backend returns { garages } or { data: { garages } }; support both
      const list = response?.garages ?? response?.data?.garages ?? (Array.isArray(response) ? response : []);
      const getGarageLocation = (ud) => {
        const gd = ud?.garageDetails || {};
        if ((gd.location || '').trim()) return (gd.location || '').trim();
        const parts = [gd.area, gd.city, gd.state, gd.country].filter(Boolean).map((s) => (s || '').toString().trim()).filter(Boolean);
        return parts.length ? parts.join(', ') : (ud.location || '').trim() || '';
      };
      const garagesData = (Array.isArray(list) ? list : []).map((user) => {
        const gd = user.garageDetails || {};
        const displayName = gd.garageName || gd.displayName || gd.ownerName || user.name || '—';
        const rawEmail = getProfileDisplayEmail(user) || getProfileDisplayEmail({ email: gd.email }) || '';
        return {
          id: user._id || user.id,
          _id: user._id || user.id,
          name: displayName,
          email: rawEmail,
          phone: user.phone || '',
          phoneCode: user.phoneCode || '+91',
          role: 'garage',
          kycStatus: user.kycStatus ?? gd.kycStatus ?? 'Pending',
          status: user.isActive !== undefined ? (user.isActive ? 'Active' : 'Inactive') : (user.status || 'Active'),
          garageDetails: user.garageDetails ?? gd,
          address: user.address || {},
          location: user.location || getGarageLocation(user),
          createdAt: user.createdAt
        };
      });
      setGarages(garagesData);
    } catch (error) {
      console.error('Failed to fetch garages:', error);
      toast.error('Failed to load garages: ' + (error.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // Expand sections when opening a garage profile
  useEffect(() => {
    if (view === 'view' && selectedGarage) {
      setExpandedAccount(true);
      setExpandedGarage(true);
    }
  }, [view, selectedGarage?.id || selectedGarage?._id]);

  const uniqueLocations = [...new Set(garages.map(g => (g.location || g.garageDetails?.location || '').trim()).filter(Boolean))].sort();

  const filteredGarages = garages.filter(garage => {
    const matchesSearch = !searchTerm ||
      garage.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (garage.email && garage.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (garage.location && garage.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (garage.phone && garage.phone.includes(searchTerm));
    const statusNorm = (garage.status || 'Active').toString().trim().toLowerCase();
    const filterNorm = statusFilter.toString().trim().toLowerCase();
    const matchesStatus = statusFilter === 'all' || filterNorm === statusNorm;
    let matchesLocation = true;
    if (locationFilter !== 'all') {
      if (typeof locationFilter === 'object' && locationFilter !== null) {
        const addr = garage.address || {};
        const gd = garage.garageDetails || {};
        const gCountry = (addr.country || gd.country || '').trim().toLowerCase();
        const gState = (addr.state || gd.state || '').trim().toLowerCase();
        const gCity = (addr.city || gd.city || '').trim().toLowerCase();
        const gArea = (addr.area || gd.area || '').trim().toLowerCase();
        const fCountry = (locationFilter.country || '').trim().toLowerCase();
        const fState = (locationFilter.state || '').trim().toLowerCase();
        const fCity = (locationFilter.city || '').trim().toLowerCase();
        const fArea = (locationFilter.area || '').trim().toLowerCase();
        if (fCountry && gCountry !== fCountry) matchesLocation = false;
        else if (fState && gState !== fState) matchesLocation = false;
        else if (fCity && gCity !== fCity) matchesLocation = false;
        else if (fArea && gArea !== fArea) matchesLocation = false;
      } else {
        const loc = (garage.location || garage.garageDetails?.location || '').trim();
        matchesLocation = loc === locationFilter;
      }
    }
    return matchesSearch && matchesStatus && matchesLocation;
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLocationFilter('all');
    toast.info('Filters cleared');
  };

  const handleGarageProfileUpdated = (garage, updatedUser) => {
    const id = garage.id || garage._id;
    const user = updatedUser?.garage || updatedUser;
    const gd = user?.garageDetails || garage.garageDetails || {};
    setGarages((prev) => prev.map((g) => (
      g.id === id || g._id === id
        ? { ...g, garageDetails: gd }
        : g
    )));
    setSelectedGarage((prev) => (
      prev && (prev.id === id || prev._id === id)
        ? { ...prev, garageDetails: gd }
        : prev
    ));
  };

  const handleKycStatusChange = async (garage, newKycStatus) => {
    const id = garage.id || garage._id;
    try {
      await mechanicGarageService.updateGarage(id, { kycStatus: newKycStatus });
      setGarages(prev => prev.map(g => (g.id === id || g._id === id) ? { ...g, kycStatus: newKycStatus } : g));
      toast.success('KYC status updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update KYC status');
    }
  };

  const handleAction = (type, garage) => {
    setSelectedGarage(garage);
    if (type === 'edit') {
      setFormData({
        name: garage.name || '',
        email: garage.email || '',
        location: garage.location || '',
        phone: garage.phone || '',
        password: '', // Don't show existing password
        status: garage.status || 'Active'
      });
      setView('edit');
    } else if (type === 'view') {
      setView('view');
    } else if (type === 'delete') {
      const deleteName = (garage.garageDetails?.garageName || garage.garageDetails?.displayName || garage.name || '').trim() || 'this garage';
      if (window.confirm(`Are you sure you want to delete ${deleteName}?`)) {
        handleDelete(garage.id || garage._id);
      }
    }
  };

  const handleDelete = async (garageId) => {
    try {
      setLoading(true);
      await mechanicGarageService.deleteGarage(garageId);
      toast.success('Garage deleted successfully!');
      fetchGarages();
    } catch (error) {
      console.error('Failed to delete garage:', error);
      toast.error('Failed to delete garage: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (view === 'edit' && selectedGarage) {
        // Update existing garage
        const garageId = selectedGarage.id || selectedGarage._id;
        // Only send password if it's provided (not empty)
        const updateData = { ...formData };
        if (!updateData.password || updateData.password.trim() === '') {
          delete updateData.password; // Don't send password if empty - keep existing password
        }
        await mechanicGarageService.updateGarage(garageId, updateData);
        toast.success('Garage updated successfully!');
        
        // Reset form and go back to list
        setView('list');
        setFormData({ name: '', email: '', location: '', phone: '', password: '', status: 'Active' });
        setSelectedGarage(null);
        fetchGarages();
      }
    } catch (error) {
      console.error('Failed to save garage:', error);
      toast.error('Failed to save garage: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getProfileImage = (g) => (g?.logo || g?.avatar || g?.profilePhoto || g?.image || g?.garageDetails?.logo || g?.garageDetails?.storeLogo || '');

  const renderDetails = (obj, expandNested = false) => {
    if (!obj || typeof obj !== 'object') return null;
    const entries = Object.entries(obj).filter(([k]) => k !== 'password' && k !== 'passwordHash');
    if (entries.length === 0) return null;
    const format = (v) => {
      if (v === undefined || v === null) return '';
      if (v === true) return 'Yes';
      if (v === false) return 'No';
      if (Array.isArray(v)) return v.length ? v.join(', ') : '';
      if (typeof v === 'object' && !expandNested) return JSON.stringify(v);
      return String(v);
    };
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {entries.map(([k, v]) => (
          <div key={k} className="p-4 bg-gray-50 rounded-2xl">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{String(k).replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}</p>
            {expandNested && typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 0 ? (
              <div className="mt-2 space-y-2">{renderDetails(v, true)}</div>
            ) : (
              <p className="font-bold text-gray-900 break-words mt-1">{format(v)}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (view === 'view' && selectedGarage) {
    const gd = selectedGarage.garageDetails || {};
    const displayName = gd.garageName || gd.displayName || gd.ownerName || '';
    const displayEmail = getProfileDisplayEmail(selectedGarage);
    const fullPhone = (selectedGarage.phoneCode || '') + (selectedGarage.phone || '');
    const supportPhone = gd.supportPhone || fullPhone;
    const handleContact = () => {
      if (displayEmail) window.location.href = `mailto:${displayEmail}`;
      else if (supportPhone) window.location.href = `tel:${supportPhone.replace(/\s/g, '')}`;
      else toast.info('No contact details available');
    };
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Garage Details</h1>
          <button
            onClick={() => setView('list')}
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
          >
            ← Back
          </button>
        </div>

        {/* Profile card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center text-white shrink-0 overflow-hidden bg-gradient-to-br from-slate-600 to-slate-700 ring-2 ring-white ring-offset-2 shadow-lg">
              {getProfileImage(selectedGarage) ? (
                <img loading="lazy" src={getProfileImage(selectedGarage)} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <FaTools className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 truncate">{displayName}</h2>
              {displayEmail ? (
                <a href={`mailto:${displayEmail}`} className="mt-1 block text-sm text-slate-600 hover:text-slate-800 hover:underline truncate transition-colors">
                  {displayEmail}
                </a>
              ) : null}
              {(selectedGarage.phone || selectedGarage.phoneCode) && (
                <a href={`tel:${fullPhone.replace(/\s/g, '')}`} className="mt-0.5 inline-block text-sm text-slate-600 hover:text-slate-800 hover:underline transition-colors">
                  {(selectedGarage.phoneCode || '') + ' ' + (selectedGarage.phone || '')}
                </a>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">
                  {selectedGarage.role || 'garage'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${selectedGarage.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {selectedGarage.status ?? '—'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Account information - collapsible */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
          <button
            type="button"
            onClick={() => setExpandedAccount((e) => !e)}
            className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FaIdCard className="text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Account Information</h3>
            </div>
            <span className="text-gray-400">{expandedAccount ? <FaChevronUp /> : <FaChevronDown />}</span>
          </button>
          {expandedAccount && (
            <div className="px-6 py-4 divide-y divide-gray-100">
              <DataRow label="Name" value={gd.garageName || gd.displayName || gd.ownerName || ''} icon={FaUser} copyable onCopy={() => toast.success('Name copied')} />
              <DataRow label="Email" value={displayEmail || '—'} icon={FaEnvelope} copyable onCopy={() => toast.success('Email copied')} />
              <DataRow label="Password" value="••••••••" icon={FaLock} />
              <DataRow label="Phone" value={selectedGarage.phone || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone copied')} />
              <DataRow label="Phone code" value={selectedGarage.phoneCode || (selectedGarage.phone ? '+91' : '') || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone code copied')} />
              <DataRow label="Role" value={getDisplayRole(selectedGarage) || selectedGarage.role || '—'} icon={FaCog} copyable onCopy={() => toast.success('Role copied')} />
            </div>
          )}
        </section>

        {/* Garage information - collapsible */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
          <button
            type="button"
            onClick={() => setExpandedGarage((e) => !e)}
            className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FaStore className="text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Garage Information</h3>
            </div>
            <span className="text-gray-400">{expandedGarage ? <FaChevronUp /> : <FaChevronDown />}</span>
          </button>
          {expandedGarage && (
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <div className="space-y-0">
                  <DataRow label="Garage name" value={gd.garageName} icon={FaStore} copyable onCopy={() => toast.success('Garage name copied')} />
                  <DataRow label="Display name" value={gd.displayName} icon={FaStore} copyable onCopy={() => toast.success('Display name copied')} />
                  <DataRow label="Owner name" value={gd.ownerName} icon={FaUser} copyable onCopy={() => toast.success('Owner name copied')} />
                  <DataRow label="Support phone" value={gd.supportPhone} icon={FaPhone} copyable onCopy={() => toast.success('Support phone copied')} />
                  <DataRow label="Year established" value={gd.yearEst} icon={FaStore} copyable onCopy={() => toast.success('Year copied')} />
                </div>
                <div className="space-y-0 sm:border-l sm:border-gray-100 sm:pl-6">
                  <DataRow label="Location" value={gd.location || selectedGarage.location} icon={FaMapMarkerAlt} copyable onCopy={() => toast.success('Location copied')} />
                  <DataRow label="Opening time" value={gd.openingTime} icon={FaClock} copyable onCopy={() => toast.success('Opening time copied')} />
                  <DataRow label="Closing time" value={gd.closingTime} icon={FaClock} copyable onCopy={() => toast.success('Closing time copied')} />
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-100">
                <DataRow label="Description" value={gd.description} icon={FaStore} copyable onCopy={() => toast.success('Description copied')} />
                <DataRow label="Services" value={gd.services} icon={FaTools} copyable onCopy={() => toast.success('Services copied')} />
                <DataRow
                  label="Service clusters"
                  value={(gd.selectedClusters || []).map((id) => CLUSTERS.find((c) => c.id === Number(id))?.label || `Cluster ${id}`)}
                  icon={FaTools}
                  copyable
                  onCopy={() => toast.success('Clusters copied')}
                />
              </div>
            </div>
          )}
        </section>

        <GarageClusterVerificationPanel
          garageId={selectedGarage.id || selectedGarage._id}
          garageDetails={gd}
          onUpdated={(updated) => handleGarageProfileUpdated(selectedGarage, updated)}
        />

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => handleAction('edit', selectedGarage)}
            className="flex-1 py-3 px-4 bg-slate-700 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-sm"
          >
            Edit garage
          </button>
          <button
            onClick={handleContact}
            className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 active:scale-[0.99] transition-all"
          >
            Contact garage
          </button>
        </div>
      </div>
    );
  }

  if (view === 'edit') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Edit Garage</h1>
          <button type="button" onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaStore className="text-gray-500" /> Account & garage details
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Update basic info and status</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Garage name *</label>
                  <input required type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Enter garage name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
                  <input required type="email" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="garage@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                  <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 1234567890" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Location</label>
                  <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} placeholder="e.g., Mumbai" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 bg-white" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password (leave empty to keep existing)</label>
                  <input type="password" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="Leave empty to keep existing password" />
                  <p className="text-xs text-gray-500 mt-1">Only enter a new password if you want to change it.</p>
                </div>
              </div>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-3">
            <button type="submit" disabled={saving} className="flex-1 py-3 px-4 bg-slate-700 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <FaSpinner className="animate-spin" />}
              Update garage
            </button>
            <button type="button" onClick={() => setView('list')} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-all">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">Garage Management</h1>
          <p className="dashboard-page-desc">View and manage all registered garages. Use filters to find by name, location, or status.</p>
        </div>
      </div>

      <div className="dashboard-filters">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
          <input
            type="text"
            placeholder="Search name, email, location, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dashboard-input w-full pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="dashboard-select"
        >
          <option value="all">All status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Pending">Pending</option>
        </select>
        <AdminLocationDropdown
          value={locationFilter === 'all' ? '' : locationFilter}
          onChange={(v) => {
            const val = v || 'all';
            setLocationFilter(val);
            try {
              if (val === 'all') localStorage.removeItem('adminLocation');
              else if (typeof v === 'object') localStorage.setItem('adminLocation', JSON.stringify(v));
              else localStorage.setItem('adminLocation', String(v));
            } catch (e) {}
          }}
          className="w-full min-w-[160px]"
        />
        <button
          type="button"
          onClick={handleClearFilters}
          disabled={!searchTerm && statusFilter === 'all' && locationFilter === 'all'}
          className="dashboard-btn-ghost inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaTimesCircle className="text-sm" /> Clear filters
        </button>
        <p className="w-full text-xs text-slate-500 mt-0 sm:mt-0 sm:w-auto sm:ml-auto">
          Showing <strong>{filteredGarages.length}</strong> of <strong>{garages.length}</strong> garage{garages.length !== 1 ? 's' : ''}
        </p>
      </div>

      {loading && (
        <div className="dashboard-card flex justify-center items-center py-16">
          <FaSpinner className="animate-spin text-primary-600 text-3xl" />
        </div>
      )}

      {!loading && filteredGarages.length === 0 && (
        <div className="dashboard-card dashboard-empty">
          <div className="dashboard-empty-icon">
            <FaTools className="text-3xl" />
          </div>
          <p className="dashboard-empty-title">No garages found</p>
          <p className="dashboard-empty-desc">
            {garages.length === 0 ? 'No garages are registered yet.' : 'Try changing your search or filters.'}
          </p>
          {(searchTerm || statusFilter !== 'all' || locationFilter !== 'all') && (
            <button type="button" onClick={handleClearFilters} className="dashboard-btn-secondary mt-4">
              Clear filters
            </button>
          )}
        </div>
      )}

      {!loading && filteredGarages.length > 0 && (
        <div className="dashboard-card p-0">
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Garage name</th>
                  <th>Owner</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Hours</th>
                  <th>KYC</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGarages.map((garage) => {
                  const gd = garage.garageDetails || {};
                  const garageName = gd.garageName || gd.displayName || gd.ownerName || garage.name || '—';
                  const ownerName = gd.ownerName || gd.displayName || garage.name || '—';
                  const phone = garage.phone || '';
                  const supportPhone = gd.supportPhone || phone;
                  const hours = [gd.openingTime, gd.closingTime].filter(Boolean).join(' – ') || '—';
                  const kyc = (garage.kycStatus || 'Pending').toString();
                  const status = (garage.status || 'Active').toString();
                  return (
                    <tr key={garage.id || garage._id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center text-white shrink-0">
                            <FaTools className="text-sm" />
                          </div>
                          <span className="font-medium text-slate-900 truncate max-w-[140px]" title={garageName}>{garageName}</span>
                        </div>
                      </td>
                      <td className="truncate max-w-[120px]" title={ownerName}>{ownerName}</td>
                      <td className="truncate max-w-[160px]" title={getProfileDisplayEmail(garage) || '—'}>{getProfileDisplayEmail(garage) || '—'}</td>
                      <td>
                        <span className="block">{phone || '—'}</span>
                        {supportPhone && supportPhone !== phone && <span className="text-xs text-slate-500">Support: {supportPhone}</span>}
                      </td>
                      <td className="max-w-[140px] truncate" title={garage.location || gd.location || ''}>{garage.location || gd.location || '—'}</td>
                      <td className="whitespace-nowrap text-slate-600">{hours}</td>
                      <td>
                        <select
                          value={kyc}
                          onChange={(e) => handleKycStatusChange(garage, e.target.value)}
                          className={`text-xs font-medium rounded-lg px-2 py-1 border cursor-pointer ${kyc === 'Verified' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : kyc === 'Rejected' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Verified">Verified</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td>
                        <span className={status.toLowerCase() === 'active' ? 'badge-success' : 'badge-warning'}>
                          {status}
                        </span>
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => handleAction('view', garage)} className="p-2.5 text-sky-600 hover:bg-sky-50 rounded-xl transition" title="View details"><FaEye className="text-sm" /></button>
                          <button type="button" onClick={() => handleAction('edit', garage)} className="p-2.5 text-emerald-600 hover:bg-emerald-50 rounded-xl transition" title="Edit"><FaEdit className="text-sm" /></button>
                          <button type="button" onClick={() => handleAction('delete', garage)} disabled={loading} className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition disabled:opacity-50" title="Delete"><FaTrash className="text-sm" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Garages;

